import React, {Component, useEffect, useRef} from 'react'
import Chart from 'chart.js/auto'
import annotationPlugin from 'chartjs-plugin-annotation'
import {getResource} from '../../../data/actions/resource'
import LocalStorage from '../../../util/localStorage'
import Resources from '../../../data/services/resources'
import {Field, FieldsManager} from '../../../data/services/fields'
import moment from 'moment-timezone'
import {checkPerm, getProp} from '../../../common/util/util-helpers'
import {currentDate, toFrontDateNoTime} from '../../../common/util/util-dates'
import AccountReportDialog from '../dialogs/account-report-dialog'
import {READ_PERM} from '../../../util/util-constants'
import {getSecondResource} from '../../../data/actions/secondResource'
import PayDateFilter from '../carrier-pay/carrier-pay-date-filter'
import {download} from '../../../data/actions/download'
import FunnelIcon from '@heroicons/react/20/solid/FunnelIcon'
import {LoaderSmall} from "../../../common/components/loader"
import Card from "../../../common/components/card"
import {ArrowPathIcon} from "@heroicons/react/24/outline"
import ModalDefault from "../../../common/components/modal/modal-default"

Chart.register(annotationPlugin)

// --- Helpers ---
const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`

// --- Stat Card ---
const StatCard = ({title, value, accent}) => (
    <div className="bg-popup border border-tm-gray-300 rounded-card p-4">
        <p className="text-xs font-medium text-tm-gray-500 uppercase tracking-wide">{title}</p>
        <p className={`text-lg font-bold mt-1 tabular-nums ${accent || 'text-tm-gray-900'}`}>{value}</p>
    </div>
)

// --- Time Series Line Chart ---
const TimeSeriesLineChart = ({points, color}) => {
    const canvasRef = useRef(null)
    const chartRef = useRef(null)

    useEffect(() => {
        if (!canvasRef.current || !points.length) return
        if (chartRef.current) chartRef.current.destroy()

        const ctx = canvasRef.current.getContext('2d')
        const avg = points.reduce((s, d) => s + d.value, 0) / points.length

        chartRef.current = new Chart(ctx, {
            type: 'line',
            data: {
                labels: points.map(d => d.label),
                datasets: [{
                    data: points.map(d => d.value),
                    borderColor: color,
                    backgroundColor: color + '14',
                    borderWidth: 2.5,
                    pointRadius: 3,
                    pointBackgroundColor: 'transparent',
                    pointBorderColor: color,
                    pointBorderWidth: 2,
                    pointHoverRadius: 6,
                    tension: 0.3,
                    fill: true,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {intersect: false, mode: 'index'},
                plugins: {
                    legend: {display: false},
                    tooltip: {
                        backgroundColor: '#fff',
                        titleColor: '#111827',
                        bodyColor: '#4b5563',
                        borderColor: '#d1d5db',
                        borderWidth: 1,
                        padding: 12,
                        cornerRadius: 8,
                        displayColors: false,
                        callbacks: {
                            label: (item) => fmt$(item.raw),
                        },
                    },
                    annotation: {
                        annotations: {
                            avgLine: {
                                type: 'line',
                                yMin: avg,
                                yMax: avg,
                                borderColor: '#9ca3af',
                                borderWidth: 1,
                                borderDash: [4, 4],
                                label: {
                                    display: true,
                                    content: `Avg ${fmt$(avg)}`,
                                    position: 'end',
                                    backgroundColor: 'transparent',
                                    color: '#9ca3af',
                                    font: {size: 11},
                                },
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        grid: {display: false},
                        ticks: {color: '#6b7280', font: {size: 11}},
                        border: {color: '#e5e7eb'},
                    },
                    y: {
                        grid: {color: '#f3f4f6'},
                        ticks: {color: '#6b7280', font: {size: 11}, callback: (v) => `$${v.toLocaleString()}`},
                        border: {display: false},
                    },
                },
            },
        })

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy()
                chartRef.current = null
            }
        }
    }, [points])

    if (!points.length) {
        return <div className="flex items-center justify-center h-full text-sm text-tm-gray-400">No data</div>
    }

    return <canvas ref={canvasRef}/>
}

// --- Vertical Bar Chart ---
const BarChart = ({labels, values, colors}) => {
    const canvasRef = useRef(null)
    const chartRef = useRef(null)

    useEffect(() => {
        if (!canvasRef.current) return
        if (chartRef.current) chartRef.current.destroy()

        const ctx = canvasRef.current.getContext('2d')
        chartRef.current = new Chart(ctx, {
            type: 'bar',
            data: {
                labels,
                datasets: [{
                    data: values,
                    backgroundColor: colors.map(c => c + '33'),
                    borderColor: colors,
                    borderWidth: 2,
                    borderRadius: 4,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {display: false},
                    tooltip: {
                        backgroundColor: '#fff',
                        titleColor: '#111827',
                        bodyColor: '#4b5563',
                        borderColor: '#d1d5db',
                        borderWidth: 1,
                        padding: 12,
                        cornerRadius: 8,
                        displayColors: false,
                        callbacks: {
                            label: (item) => fmt$(item.raw),
                        },
                    },
                },
                scales: {
                    x: {
                        grid: {display: false},
                        ticks: {color: '#6b7280', font: {size: 12}},
                        border: {color: '#e5e7eb'},
                    },
                    y: {
                        grid: {color: '#f3f4f6'},
                        ticks: {color: '#6b7280', font: {size: 11}, callback: (v) => `$${v.toLocaleString()}`},
                        border: {display: false},
                    },
                },
            },
        })

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy()
                chartRef.current = null
            }
        }
    }, [values])

    return <canvas ref={canvasRef}/>
}

// --- Doughnut Chart ---
const DoughnutChart = ({labels, values, colors}) => {
    const canvasRef = useRef(null)
    const chartRef = useRef(null)

    useEffect(() => {
        if (!canvasRef.current) return
        if (chartRef.current) chartRef.current.destroy()

        const ctx = canvasRef.current.getContext('2d')
        chartRef.current = new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels,
                datasets: [{
                    data: values,
                    backgroundColor: colors.map(c => c + '33'),
                    borderColor: colors,
                    borderWidth: 2,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        position: 'right',
                        labels: {color: '#6b7280', font: {size: 12}, padding: 16},
                    },
                    tooltip: {
                        backgroundColor: '#fff',
                        titleColor: '#111827',
                        bodyColor: '#4b5563',
                        borderColor: '#d1d5db',
                        borderWidth: 1,
                        padding: 12,
                        cornerRadius: 8,
                        callbacks: {
                            label: (item) => ` ${fmt$(item.raw)}`,
                        },
                    },
                },
            },
        })

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy()
                chartRef.current = null
            }
        }
    }, [values])

    return <canvas ref={canvasRef}/>
}

// --- Section wrapper ---
const Section = ({title, height = 280, children}) => (
    <div>
        <p className="text-xs font-semibold text-tm-gray-500 uppercase tracking-wide mb-2">{title}</p>
        <div className="bg-popup border border-tm-gray-300 rounded-card p-4" style={{height}}>
            {children}
        </div>
    </div>
)

export default class AccountingDashboardTab extends Component {

    constructor(props) {
        super(props)
        this.state = {
            fields: {
                StartDate: new Field('StartDate', moment().startOf('year').format('YYYY-MM-DD HH:mm:ss'), ['date'], false, 'date', {
                    addContainerClass: 'col-span-4'
                }, {}),
                EndDate: new Field('EndDate', moment().format('YYYY-MM-DD HH:mm:ss'), ['date'], false, 'date', {
                    addContainerClass: 'col-span-4'
                }, {})
            },
            accountDetailsDialog: false,
            selectedItem: null,
            datesFilterDialogOpen: false
        }
    }

    /** Lifecycle
     ================================================================= */
    componentDidMount = () => {
        this.fetchData()
    }

    /** Data Events
     ================================================================= */
    fetchData = () => {
        this.props.dispatch(getResource({
            user: LocalStorage.get('user'),
            resource: Resources.AccountingDashboard,
            query: FieldsManager.getFieldKeyValues(this.state.fields)
        }))
    }

    downloadExcelReports = (query) => {
        this.props.dispatch(download({
            user: LocalStorage.get('user'),
            resource: Resources.AccountingAccountReport,
            query: Object.assign({
                format: 'EXCEL',
                name: 'income_statement_transactions_' + currentDate() + '.xlsx'
            }, query)
        }))
    }

    /** UI Events
     ================================================================= */
    handleInputChange = (name, value) => {
        this.setState({fields: FieldsManager.updateField(this.state.fields, name, value)}, this.fetchData)
    }

    handleToggleAccountDetails = (selectedItem) => {
        this.setState({
            selectedItem: selectedItem,
            accountDetailsDialog: !this.state.accountDetailsDialog
        })
    }

    handleToggleFilterDialog = () => {
        this.setState({datesFilterDialogOpen: !this.state.datesFilterDialogOpen})
    }

    /** Render
     ================================================================= */
    render() {
        const {translate} = this.props
        const isLoading = this.props.resource?.isLoading

        const styles = typeof document !== 'undefined' ? getComputedStyle(document.documentElement) : null
        const primaryColor = styles?.getPropertyValue('--color-primary').trim() || '#4f46e5'

        const profitAndLoss = getProp(this.props, 'resource.data.ProfitAndLoss', {})
        const invoices = getProp(this.props, 'resource.data.Invoices', {})

        const toPoints = (arr) => (arr ?? []).map(it => ({
            label: toFrontDateNoTime(it[0]),
            value: Number(it[1]) || 0
        }))

        const incomePoints = toPoints(getProp(this.props.resource, 'data.Sales', []))
        const receivablePoints = toPoints(getProp(this.props.resource, 'data.Receivables', []))
        const payablePoints = toPoints(getProp(this.props.resource, 'data.Payable', []))

        const {StartDate, EndDate} = this.state.fields
        const dateLabel = `${toFrontDateNoTime(StartDate.value)} – ${toFrontDateNoTime(EndDate.value)}`

        return (
            <Card>
                {/* Header */}
                <div className="flex items-center justify-between px-1 pb-4">
                    <span className="text-xs text-tm-gray-400">{dateLabel}</span>
                    <div className="flex items-center gap-2">
                        <button
                            className="btn btn-icon"
                            title={translate('text.Filters')}
                            onClick={this.handleToggleFilterDialog}
                        >
                            <FunnelIcon className="h-4 w-4"/>
                        </button>
                        <button
                            className="btn btn-header"
                            onClick={this.fetchData}
                        >
                            <ArrowPathIcon className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`}/>
                        </button>
                    </div>
                </div>

                {isLoading && (
                    <div className="flex items-center justify-center py-20">
                        <LoaderSmall/>
                    </div>
                )}

                {!isLoading && (
                    <div className="space-y-6 pb-8">
                        {/* Stat cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                            <StatCard
                                title="Income"
                                value={fmt$(profitAndLoss.Income)}
                                accent="text-green-600"
                            />
                            <StatCard
                                title="Expense"
                                value={fmt$(profitAndLoss.Expense)}
                                accent="text-red-500"
                            />
                            <StatCard
                                title="Net Income"
                                value={fmt$(profitAndLoss.NetIncome)}
                                accent={Number(profitAndLoss.NetIncome) >= 0 ? 'text-green-600' : 'text-red-500'}
                            />
                            <StatCard
                                title="Open Invoices"
                                value={fmt$(invoices.Open)}
                            />
                            <StatCard
                                title="Overdue Invoices"
                                value={fmt$(invoices.Overdue)}
                                accent="text-red-500"
                            />
                        </div>

                        {/* Income line */}
                        <Section title="Income over time">
                            <TimeSeriesLineChart points={incomePoints} color="#16a34a"/>
                        </Section>

                        {/* P&L bar | Invoices doughnut */}
                        <div className="grid grid-cols-2 gap-6">
                            <Section title="Profit & Loss">
                                <BarChart
                                    labels={['Income', 'Expense', 'Net Income']}
                                    values={[
                                        Number(profitAndLoss.Income) || 0,
                                        Number(profitAndLoss.Expense) || 0,
                                        Number(profitAndLoss.NetIncome) || 0
                                    ]}
                                    colors={['#16a34a', '#ef4444', primaryColor]}
                                />
                            </Section>
                            <Section title="Invoices">
                                <DoughnutChart
                                    labels={['Open', 'Overdue', 'Paid']}
                                    values={[
                                        Number(invoices.Open) || 0,
                                        Number(invoices.Overdue) || 0,
                                        Number(invoices.Paid) || 0
                                    ]}
                                    colors={['#3b82f6', '#f59e0b', '#16a34a']}
                                />
                            </Section>
                        </div>

                        {/* Receivables line */}
                        <Section title="Receivables over time">
                            <TimeSeriesLineChart points={receivablePoints} color="#3b82f6"/>
                        </Section>

                        {/* Payable line */}
                        <Section title="Payable over time">
                            <TimeSeriesLineChart points={payablePoints} color="#ef4444"/>
                        </Section>
                    </div>
                )}

                <AccountReportDialog
                    AccountID={this.state.selectedItem?.AccountID}
                    AccountName={this.state.selectedItem?.AccountName}
                    StartDate={this.state.fields.StartDate?.value}
                    EndDate={this.state.fields.EndDate?.value}
                    onClose={this.handleToggleAccountDetails}
                    show={checkPerm(Resources.AccountingAccountReport, READ_PERM) && this.state.accountDetailsDialog}
                    translate={this.props.translate}
                    getTransactions={(query) => {
                        this.props.dispatch(getSecondResource({
                            user: LocalStorage.get('user'),
                            resource: Resources.AccountingAccountReport,
                            query: query
                        }))
                    }}
                    downloadExcelReports={(query) => this.downloadExcelReports(query)}
                    isLoading={getProp(this.props, 'secondResource.isLoading', false)}
                    transactions={getProp(this.props, 'secondResource.data', [])}
                    thirdResource={this.props.thirdResource}
                    dialogResource={this.props.dialogResource}
                    dispatch={this.props.dispatch}
                />

                <ModalDefault
                    show={!!this.state.datesFilterDialogOpen}
                    widthClass="max-w-3xl"
                    title="Date filter"
                    closeButtonLabel={translate('btn.close')}
                    onClose={this.handleToggleFilterDialog}
                    translate={translate}
                >
                    <PayDateFilter
                        queryFields={this.state.fields}
                        translate={translate}
                        updateQueryFields={(queryFieldsUpdate) => {
                            this.setState({fields: queryFieldsUpdate}, this.fetchData)
                        }}
                        hasDateType={false}
                        isDateTypeClearable={false}
                        isClearDatesButtonVisible={false}
                        onQueryChange={this.handleInputChange}
                    />
                </ModalDefault>
            </Card>
        )
    }
}
